<template>
  <DialogCmp :showDialog="true"
             dialog-class="staff__dialog"
             :close-icon="!isMobile"
             @click-close="close"
             @click-outside="close">
    <template slot="header">{{$t(`${tStaff}${addMode ? 'title_add' : 'title_add_remove'}`)}}</template>
    <template slot="content">
      <p class="staff__subtitle">{{$t(`${tStaff}${addMode ? 'subtitle_add' : 'subtitle_add_remove'}`)}}</p>
      <div class="staff__select-container">
        <FilterMultiselect class="filter-selection"
                           checkbox
                           :disabled="saving"
                           :items="parseAvailableStaff"
                           :value="this.selectedStaff"
                           :hint="$t(`${tStaff}select_placeholder`)"
                           item-text="display_name"
                           item-id="uid"
                           @input="updateSelectedStaff"/>
      </div>
      <div class="staff__comming-meeting" v-if="meetingsWith.length > 0">
        <span class="staff__warning">
          <v-icon small color="#ffc511">icon-Warning-fill</v-icon>
        </span>
        <p :key="removedStaff.length">
          {{matter.display_name}} {{$t(`${tStaff}meeting_warning`)}}: {{meetingsWith}}
        </p>
      </div>
    </template>
    <template slot="footer">
      <v-btn :disabled="saving" text @click="close">{{$t('common.dialog.cancel')}}</v-btn>
      <v-btn :disabled="saving" class="staff__confirm" :color="brandColor" elevation="0" @click="save">{{$t(`${tDialog}${addMode ? 'add' : 'save'}`)}}</v-btn>
    </template>
  </DialogCmp>
</template>

<script>
import DialogCmp from "@/modules/common/components/dialog/DialogCmp";
import {AvatarImage} from "@/modules/common/components";
import {mapGetters} from "vuex";
import FilterMultiselect from "@/modules/common/components/filter/multiselect/FilterMultiselect";
import {matterService} from "@/modules/matters/services/matterService";

export default {
  name: 'MatterAdditionalStaffAssignDialog',
  components: {DialogCmp, AvatarImage, FilterMultiselect},
  props: {
    addMode: {
      type: Boolean,
      require: true
    },
    additionalStaff: {
      type: Array
    },
    availableStaff: {
      type: Array
    },
    matter: {
      type: Object
    }
  },
  data() {
    return {
      tStaff: 'staff.client_assign.dialog.',
      tDialog: 'common.dialog.',
      saving: false,
      parseAvailableStaff: [],
      selectedStaff:[],
      meetingState: [],
      removedStaff: {},
      removedStaffArr: []
    }
  },
  async created() {
    if(this.upcomingBookings.length === 0) {
      await this.$store.dispatch({
        type: "mattersModule/loadUpcomingBookings",
        payload: {clientId: this.currContact.uid, page: 1}
      })
    }
    this.parsingAvailableStaff();
  },
  computed: {
    ...mapGetters(['isMobile']),
    ...mapGetters('accountModule', ['business', 'account']),
    ...mapGetters('mattersModule', ['upcomingBookings', 'currContact']),
    isPane() {
      return this.$store.getters.isPane;
    },
    meetingsWith() {
      return this.removedStaffArr.filter(staff => staff.meetings).map(staff => staff.display_name).join(', ')
    },
    brandColor() {
      return this.account.brand.secondary_color || '#0093B8';
    }
  },
  methods: {
    updateSelectedStaff(collaborators) {
      const remove = this.selectedStaff.filter(staff => !collaborators.find(collaborator => staff.uid === collaborator.uid))[0];
      if(remove) this.removedStaff[remove.uid] = remove

      collaborators.forEach(collaborator => {
        if(this.removedStaff[collaborator.uid]){
          delete this.removedStaff[collaborator.uid];
        }
      })

      this.removedStaffArr = Object.values(this.removedStaff);
      this.selectedStaff = [...collaborators]
    },
    parsingAvailableStaff() {
      this.parseAvailableStaff = this.availableStaff.map(staff => {
        const parseStaff = {...staff}
        if (!!this.additionalStaff.find(assignee => assignee.uid === staff.uid)) {
          this.selectedStaff.push(parseStaff);
        }

        const meetingsForStaff = this.upcomingBookings.filter(booking => booking.staff.uid === staff.uid)
        parseStaff.meetings = meetingsForStaff.length;

        return parseStaff;
      });
    },
    save() {
      const trackEvent = this.addMode ? 'Additional staff added' : 'Additional staff updated';
      this.$track(trackEvent);
      this.updateCollaborators();
    },
    close() {
      this.$emit('close-dialog');
    },
    removeStaff(collaborator) {
      this.selectedStaff = this.selectedStaff.filter(staff => staff !== collaborator)
    },
    async updateCollaborators() {
      this.saving = true;

      const collaboratorsUids = this.selectedStaff.map(collaborator => collaborator.uid);
      const removeList = [];
      const addList = [];

      this.availableStaff.forEach(staff => {
        if(collaboratorsUids.indexOf(staff.uid) > -1) {
          if(!this.additionalStaff.find(additional => additional.uid === staff.uid)){
            addList.push(staff.uid)
          }
        } else {
          if(this.additionalStaff.find(additional => additional.uid === staff.uid)) {
            removeList.push(staff.uid)
          }
        }
      });

      const collaboratorsReassign = await Promise.all([
        ...addList.map(collaborator => matterService.addCollaborators(this.matter.uid, collaborator)),
        ...removeList.map(collaborator => matterService.removeCollaborators(this.matter.uid, collaborator))
      ]);

      if(collaboratorsReassign.error) {
        this.$emit('update-collaborators',{isSuccess: false});
      } else {
        this.$emit('update-collaborators',{isSuccess: true});
      }

      this.saving = false;
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_mixins.scss";

.staff {
  &__dialog {
    font-family: var(--primary-font-family);
  }

  &__subtitle {
    font-size: 14px;
    @include sm-and-down {
      margin-bottom: 10px;
    }
  }

  &__chip {
    border: 1px solid $gray_color-16;
    margin: 0 5px 8px 0;
    padding: 4px 8px 4px 2px;

    ::v-deep .v-icon {
      color: $gray_color-16;
    }
    &:hover ::v-deep .v-icon {
      color: $black-color-1;
      transition: color .2s;
    }

    .v-chip__content {
      font-size: 12px;
    }
  }

  &-avatar {
    margin-inline-end: 8px;
  }

  &__select {
    font-family: var(--primary-font-family);

    &-container {
      position: relative;
      margin-top: 20px;
      width: 100%;
    }

    &--override-placeholder {
      position: absolute;
      left: 0;
      top: 18px;
      color: $grey-color-5;
    }
  }

  &__assignee {
    &--list {
      list-style-type: none;
      padding: 0;
      display: flex;
      flex-wrap: wrap;
    }
  }

  &__comming-meeting {
    font-size: 12px;
    display: flex;
    margin-top: 15px;
  }

  &__confirm {
    color: $white-color-1
  }

  &__warning {
    margin-inline-end: 8px;
  }
}
::v-deep {
  .selected-area .selected-item {
    margin-top: 8px;
  }

  .v-select {
    margin-top: 20px;
  }

  .v-btn__content {
    text-transform: none;
  }
}
</style>
